import { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import EnvBanner from '../EnvBanner';
import { cn } from '../../utils/cn';

/**
 * Application shell with sidebar, topbar, and main content area.
 * Provides responsive layout with collapsible sidebar on mobile.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} [props.className] - Additional classes for main content
 */
export function AppShell({ children, className }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Refs for focus management
  const hamburgerButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const mobileSidebarRef = useRef(null);

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, []);

  // Focus management: focus close button when drawer opens
  useEffect(() => {
    if (sidebarOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [sidebarOpen]);

  // Close drawer and return focus to hamburger
  const closeDrawer = useCallback(() => {
    setSidebarOpen(false);
    // Return focus to hamburger button after close
    requestAnimationFrame(() => {
      if (hamburgerButtonRef.current) {
        hamburgerButtonRef.current.focus();
      }
    });
  }, []);

  // Handle Escape key to close mobile drawer
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && sidebarOpen) {
        closeDrawer();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen, closeDrawer]);

  // Focus trap for mobile drawer
  useEffect(() => {
    if (!sidebarOpen || !mobileSidebarRef.current) return;

    const handleTabKey = (event) => {
      if (event.key !== 'Tab') return;

      const focusableElements = mobileSidebarRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab: if on first element, wrap to last
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last element, wrap to first
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [sidebarOpen]);

  // Close mobile sidebar when clicking outside
  const handleOverlayClick = () => {
    closeDrawer();
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Skip link for keyboard accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:bg-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-md focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
      >
        Skip to content
      </a>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <div
        ref={mobileSidebarRef}
        className={cn(
          'fixed inset-y-0 left-0 z-50 lg:hidden transition-transform duration-200',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <Sidebar />
        <button
          ref={closeButtonRef}
          onClick={closeDrawer}
          className="absolute top-4 right-4 p-1.5 rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          aria-label="Close sidebar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* Main content area */}
      <div
        className={cn(
          'flex flex-col min-h-screen transition-all duration-200',
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        )}
      >
        <TopBar onMenuClick={() => setSidebarOpen(true)} hamburgerRef={hamburgerButtonRef} />
        <EnvBanner />

        <main id="main-content" className={cn('flex-1 p-4 sm:p-6', className)}>
          {children}
        </main>
      </div>
    </div>
  );
}
